import type { OccurrencePurpose } from "../interface/occurrences"

export interface MediaLocation {
  templateId?: string
  versionId?: string
  occurrenceId?: string
  purpose?: OccurrencePurpose
  placementId?: string | null
  position?: number
}

export interface PostBotErrorMeta {
  expectedRevision?: number
  currentRevision?: number
  occurrenceId?: string
  unknown?: boolean
  locations?: MediaLocation[]
}

export interface PostBotErrorDetail {
  code: string
  document: string
  path: string
  placementId: string | null
  message: string
  meta?: PostBotErrorMeta
}

export interface PostBotError {
  source: "feature" | "legacy"
  status?: number | string
  code?: string
  message: string
  requestId?: string
  details: PostBotErrorDetail[]
  meta?: PostBotErrorMeta
}

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

/** null means invalid; [] is the valid root pointer. Decode ~1 before ~0. */
export const decodePointer = (path: unknown): string[] | null => {
  if (typeof path !== "string") return null
  if (path === "") return []
  if (!path.startsWith("/") || /~(?:[^01]|$)/.test(path)) return null
  return path
    .slice(1)
    .split("/")
    .map((segment) => segment.replace(/~1/g, "/").replace(/~0/g, "~"))
}

const isRevision = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 1

const readLocation = (value: unknown): MediaLocation | undefined => {
  if (!record(value)) return undefined
  const location: MediaLocation = {}
  if (typeof value.templateId === "string")
    location.templateId = value.templateId
  if (typeof value.versionId === "string") location.versionId = value.versionId
  if (typeof value.occurrenceId === "string")
    location.occurrenceId = value.occurrenceId
  if (
    value.purpose === "scheduled" ||
    value.purpose === "preview" ||
    value.purpose === "legacy"
  )
    location.purpose = value.purpose
  if (typeof value.placementId === "string" || value.placementId === null)
    location.placementId = value.placementId
  if (
    typeof value.position === "number" &&
    Number.isSafeInteger(value.position) &&
    value.position >= 0
  )
    location.position = value.position
  return location.templateId || location.versionId || location.occurrenceId
    ? location
    : undefined
}

const readMeta = (value: unknown): PostBotErrorMeta | undefined => {
  if (!record(value)) return undefined
  const meta: PostBotErrorMeta = {}
  if (isRevision(value.expectedRevision))
    meta.expectedRevision = value.expectedRevision
  if (isRevision(value.currentRevision))
    meta.currentRevision = value.currentRevision
  if (typeof value.occurrenceId === "string")
    meta.occurrenceId = value.occurrenceId
  if (typeof value.unknown === "boolean") meta.unknown = value.unknown
  if (Array.isArray(value.locations)) {
    const locations = value.locations
      .map(readLocation)
      .filter((item): item is MediaLocation => item !== undefined)
    if (locations.length || !value.locations.length) meta.locations = locations
  }
  return meta
}

const isDetail = (value: unknown): value is Record<string, unknown> =>
  record(value) &&
  typeof value.code === "string" &&
  typeof value.document === "string" &&
  typeof value.path === "string" &&
  decodePointer(value.path) !== null &&
  (value.placementId === null || typeof value.placementId === "string") &&
  typeof value.message === "string"

const messageFrom = (value: unknown): string | undefined => {
  if (typeof value === "string" && value) return value
  if (Array.isArray(value) && value.every((item) => typeof item === "string"))
    return value.join("; ") || undefined
  return undefined
}

/** Accept RTK fetch errors, direct feature envelopes, bulk item errors and retained failures. */
export const readPostBotError = (error: unknown): PostBotError => {
  const outer = record(error) ? error : {}
  const status =
    typeof outer.originalStatus === "number"
      ? outer.originalStatus
      : typeof outer.status === "number" || typeof outer.status === "string"
        ? outer.status
        : undefined
  const data = record(outer.data) ? outer.data : outer
  const nested = record(data.error) ? data.error : undefined
  const feature = nested ?? data
  const isBulk = data === outer && !nested && typeof feature.status === "number"
  if (
    (nested !== undefined || isBulk) &&
    typeof feature.code === "string" &&
    (typeof feature.message === "string" || isBulk) &&
    Array.isArray(feature.details) &&
    feature.details.every(isDetail)
  ) {
    return {
      source: "feature",
      status,
      code: feature.code,
      message: messageFrom(feature.message) ?? "PostBot request failed.",
      requestId:
        typeof feature.requestId === "string" ? feature.requestId : undefined,
      details: feature.details.map((detail) => ({
        code: detail.code as string,
        document: detail.document as string,
        path: detail.path as string,
        placementId: detail.placementId as string | null,
        message: detail.message as string,
        meta: readMeta(detail.meta),
      })),
      meta: readMeta(feature.meta),
    }
  }
  return {
    source: "legacy",
    status,
    code: undefined,
    details: [],
    message:
      messageFrom(data.message) ??
      messageFrom(data.error) ??
      messageFrom(outer.message) ??
      messageFrom(outer.error) ??
      "PostBot request failed.",
  }
}
